/**
 * Cart API Composable
 * 
 * Handles all API interactions for Add to Cart process.
 * Includes: backend API, Shopify cart API, cart data fetching
 */

import axios from 'axios';
import { formatLineItemsFeeForCart } from '../utils/payloadBuilders.js';

/**
 * Cart API composable
 * 
 * @returns {Object} API methods
 */
export function useCartAPI() {
    /**
     * Get API base URL from window config
     */
    const getApiBaseUrl = () => {
        return window.PXUTheme?.theme_settings?.api_beckend_url || 
               "https://dev.api-customapp.comda.com/";
    };

    /**
     * Get shop domain from window config
     */
    const getShopDomain = () => {
        return window.PXUTheme?.theme_settings?.api_shop_domain || 
               'devcomda.myshopify.com';
    };

    /**
     * Get shop name from domain
     */
    const getShopName = () => {
        const domain = getShopDomain();
        return domain.replace('.myshopify.com', '');
    };

    /**
     * Parse JSON response from string
     * 
     * @param {string} stringResponse - String to parse
     * @returns {Object|null} Parsed JSON or null
     */
    const getJsonResponseFromString = (stringResponse) => {
        try {
            const cutKey = 'responesVariantsList';
            const indexOfCutKey = stringResponse.indexOf(cutKey);

            if (indexOfCutKey === -1) {
                console.error('[CartAPI] responesVariantsList key not found in string response');
                return null;
            }

            const cutedData = stringResponse.slice(indexOfCutKey);
            const jsonTypeResponse = '{"' + cutedData;

            const formatedStr = jsonTypeResponse
                .replace(/\\"\s*W/g, '\\" W')
                .replace(/\\"\s*H/g, '\\" H');

            const jsonResponse = JSON.parse(formatedStr);
            console.warn('[CartAPI] getJsonResponseFromString - response was a STRING');

            if (cutKey in jsonResponse) {
                return jsonResponse[cutKey];
            }

            console.error('[CartAPI] responesVariantsList not found after parse');
            return null;
        } catch (e) {
            console.error('[CartAPI] getJsonResponseFromString failed to parse JSON response:', e);
            return null;
        }
    };

    /**
     * Get current cart data from Shopify
     * 
     * @param {Object} cartObject - Cart object reference to update
     * @param {Object} responsedCartItems - Responded cart items reference to update
     * @returns {Promise<Object|null>} Cart data or null
     */
    const getFromCart = async (cartObject, responsedCartItems) => {
        try {
            const response = await axios.get(window.Shopify.routes.root + 'cart.js');
            console.log('[CartAPI] Current cart:', response.data);
            
            // Save cart items to localStorage for cart page
            if (response.data && response.data.items) {
                try {
                    cartObject.value = response.data;
                    updateCartItemsInCartObject(cartObject, responsedCartItems);
                    console.log('[CartAPI] Cart items saved to localStorage:', response.data.items.length);
                } catch (storageError) {
                    console.error('[CartAPI] Failed to save cart items to localStorage:', storageError);
                }
            }
            
            return response.data;
        } catch (error) {
            console.error('[CartAPI] Failed to fetch cart:', error);
            return null;
        }
    };

    /**
     * Update cart items in cart object with responsed items
     * 
     * @param {Object} cartObject - Cart object reference
     * @param {Object} responsedCartItems - Responded cart items reference
     */
    const updateCartItemsInCartObject = (cartObject, responsedCartItems) => {
        if (cartObject.value.items?.length && responsedCartItems.value) {
            console.log('[CartAPI] Updating cart items in cart object');
            
            cartObject.value.items = cartObject.value.items.map(item => {
                if (!item.image && responsedCartItems.value[item.variant_id]) {
                    return responsedCartItems.value[item.variant_id];
                }
                return item;
            });
            
            cartObject.value.updated_at = new Date().getTime();
            
            // Refresh mini cart if available
            if (window.miniCart) {
                window.miniCart.refreshMiniCart(cartObject.value);
            }
            
            // Save to localStorage
            localStorage.setItem('cartItems', JSON.stringify(cartObject.value));
        }
    };

    /**
     * Fire-and-forget log line to the backend's /api/add-log endpoint
     * (Laravel LogController -> Log::debug / Log::critical).
     *
     * Centralizing this here means every ATC frontend log (critical cart/add.js
     * failures, sendToBackend request/response, etc.) goes through one path and
     * can be grepped in backend logs by corrId regardless of scope.
     *
     * Never throws, never awaited, never blocks the cart flow.
     *
     * @param {string} type - 'critical' | 'debug'
     * @param {string} scope - log scope/category (free text, shows up in backend log line)
     * @param {string} message - log message
     * @param {string} corrId - correlation id (X-Client-Request-Id), e.g. "ATC-..."
     */
    const logToBackend = (type, scope, message, corrId) => {
        try {
            // Do not await — logging must not delay or break the cart flow.
            axios.post(
                getApiBaseUrl() + 'api/add-log',
                { type, scope, message },
                { headers: { 'X-Client-Request-Id': corrId } }
            ).catch(() => { /* swallow: never block cart flow on logging */ });
        } catch (e) {
            // swallow: reporting must never throw
        }
    };

    /**
     * Report a /cart/add.js failure to the backend as a CRITICAL log.
     *
     * New production failure mode: the backend creates a temp variant and returns its id,
     * but the storefront cart/add.js cannot find that variant yet
     * ({status:422, message:"Cart Error", description:"Cannot find variant"}).
     * Backend logs do NOT catch it (the id WAS returned), so we surface it here.
     * A CRITICAL line in CloudWatch (/ecs/prod-imprintapp-td) triggers the existing
     * Grafana rule "CriticalLogsDetected-ECS-prod-imprintapp".
     *
     * Fire-and-forget: never throws, never blocks the cart flow.
     *
     * @param {Error} error - axios error thrown by cart/add.js
     * @param {Array} items - items sent in the failed batch
     * @param {string} corrId - correlation id (X-Client-Request-Id), e.g. "ATC-..."
     * @param {string} batch - 'first' | 'second'
     */
    const reportAddToCartCritical = (error, items, corrId, batch) => {
        try {
            const status = error?.response?.status ?? 'n/a';
            const shopifyMsg = error?.response?.data?.message || '';
            const description = error?.response?.data?.description || error?.message || 'unknown';
            const variantIds = Array.isArray(items)
                ? items.map(i => i?.id).filter(Boolean)
                : [];
            const groupID = (Array.isArray(items) && items[0]?.properties?._groupID) || '';

            const message =
                `cart/add.js failed (batch=${batch}) status=${status} ` +
                `shopify="${shopifyMsg}: ${description}" ` +
                `variants=[${variantIds.join(',')}] group=${groupID} corr=${corrId}`;

            logToBackend('critical', 'cart-add', message, corrId);
        } catch (e) {
            // swallow: reporting must never throw
        }
    };

    // Shopify storefront has eventual-consistency lag after productVariantsBulkCreate.
    // A 422 "Cannot find variant" means the variant id is valid but not yet visible to
    // the storefront cart. Retrying after a short pause resolves it in practice.
    const CART_ADD_MAX_ATTEMPTS = 5;
    const CART_ADD_RETRY_BASE_MS = 700; // attempt N waits N * 700ms (0.7s, 1.4s, 2.1s, 2.8s)

    const _isVariantNotFound = (error) =>
        error?.response?.status === 422 &&
        error?.response?.data?.description === 'Cannot find variant';

    const _delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    /**
     * Add items to cart (first batch - usually fees)
     * Retries up to CART_ADD_MAX_ATTEMPTS times on "Cannot find variant" 422.
     * CRITICAL is only reported when all attempts are exhausted.
     *
     * @param {Array} items - Items to add
     * @param {string} corrId - Correlation ID for tracking
     * @param {Object} responsedCartItems - Responded cart items reference to update
     * @returns {Promise<Object>} Response data
     */
    const addToCartFirst = async (items, corrId, responsedCartItems) => {
        console.log('[CartAPI] addToCartFirst - adding', items.length, 'items');

        for (let attempt = 1; attempt <= CART_ADD_MAX_ATTEMPTS; attempt++) {
            try {
                const response = await axios.post(
                    window.Shopify.routes.root + 'cart/add.js',
                    { items },
                    { headers: { 'X-Client-Request-Id': corrId } }
                );

                response.data.items.forEach(item => {
                    if (!responsedCartItems.value[item.variant_id]) {
                        responsedCartItems.value[item.variant_id] = item;
                    }
                });

                if (attempt > 1) {
                    console.log(`[CartAPI] addToCartFirst - succeeded on attempt ${attempt}`);
                }
                return response.data;

            } catch (error) {
                if (_isVariantNotFound(error) && attempt < CART_ADD_MAX_ATTEMPTS) {
                    const wait = attempt * CART_ADD_RETRY_BASE_MS;
                    console.warn(`[CartAPI] addToCartFirst - attempt ${attempt} "Cannot find variant", retrying in ${wait}ms`);
                    await _delay(wait);
                    continue;
                }
                console.error('[CartAPI] addToCartFirst - error:', error);
               // reportAddToCartCritical(error, items, corrId, 'first');
                throw error;
            }
        }
    };

    /**
     * Add items to cart (second batch - usually products)
     * Retries up to CART_ADD_MAX_ATTEMPTS times on "Cannot find variant" 422.
     * CRITICAL is only reported when all attempts are exhausted.
     *
     * @param {Array} items - Items to add
     * @param {string} corrId - Correlation ID for tracking
     * @param {Object} responsedCartItems - Responded cart items reference to update
     * @returns {Promise<Object>} Response data
     */
    const addToCartSecond = async (items, corrId, responsedCartItems) => {
        if (!items || items.length === 0) {
            console.warn('[CartAPI] addToCartSecond called with empty items array');
            return null;
        }

        console.log('[CartAPI] addToCartSecond - adding', items.length, 'items');

        for (let attempt = 1; attempt <= CART_ADD_MAX_ATTEMPTS; attempt++) {
            try {
                const response = await axios.post(
                    window.Shopify.routes.root + 'cart/add.js',
                    { items },
                    { headers: { 'X-Client-Request-Id': corrId } }
                );

                if (response.data.items) {
                    response.data.items.forEach(item => {
                        if (!responsedCartItems.value[item.variant_id]) {
                            responsedCartItems.value[item.variant_id] = item;
                        }
                    });
                }

                if (attempt > 1) {
                    console.log(`[CartAPI] addToCartSecond - succeeded on attempt ${attempt}`);
                }
                return response.data;

            } catch (error) {
                if (_isVariantNotFound(error) && attempt < CART_ADD_MAX_ATTEMPTS) {
                    const wait = attempt * CART_ADD_RETRY_BASE_MS;
                    console.warn(`[CartAPI] addToCartSecond - attempt ${attempt} "Cannot find variant", retrying in ${wait}ms`);
                    await _delay(wait);
                    continue;
                }
                console.error('[CartAPI] addToCartSecond - error:', error);
                reportAddToCartCritical(error, items, corrId, 'second');
                throw error;
            }
        }
    };

    /**
     * Send payload to backend API for processing
     *
     * Logs the outcome (success or failure) of each attempt to the backend
     * via /api/add-log. No separate pre-request log is sent — the backend's
     * own LogAllRequests middleware already logs every incoming request
     * (ATC_LOG_REQUEST) unconditionally, before routing/controller, keyed by
     * the same corrId (X-Client-Request-Id) — so "did this request reach the
     * backend at all" is already answered there without duplicating it here.
     *
     * The known intermittent production failure this exists to diagnose: the
     * backend returns an HTML error page with no CORS headers instead of
     * JSON (see notes in the catch block below). When that happens the
     * browser reports an opaque "CORS error" and axios cannot read
     * status/body/headers at all.
     *
     * Retries up to SEND_TO_BACKEND_MAX_ATTEMPTS times on ANY failure (opaque
     * CORS/network error or a normal HTTP error alike). All attempts share
     * the same corrId (it identifies the one user action end-to-end through
     * addToCartFirst/addToCartSecond too); each attempt is distinguished in
     * the logs by an `attempt=N/MAX` field instead. Attempts 1..MAX-1 log as
     * 'debug' (a retry that then succeeds is not an incident); only the
     * final exhausted attempt logs as 'critical'.
     *
     * KNOWN RISK: this endpoint is not known to be idempotent per corrId —
     * it allocates temp Shopify variants/fees (TemporaryProductAllocator)
     * server-side. If a request actually succeeds on the backend but its
     * response never reaches the browser (the opaque CORS/network case),
     * retrying will call it again and may create duplicate temp
     * variants/fees. Retrying anyway per explicit product decision — flagged
     * here for whoever revisits this.
     *
     * @param {Object} apiData - Data to send to backend
     * @param {string} corrId - Correlation ID for tracking
     * @param {string} groupID - Current group ID
     * @param {Object} feeSkus - Fee SKUs from product imprint_data (setup_fee_sku, running_fee_sku, etc.)
     * @returns {Promise<Object>} Processed line items from backend
     */
    const SEND_TO_BACKEND_MAX_ATTEMPTS = 3;
    const SEND_TO_BACKEND_RETRY_BASE_MS = 700; // attempt N waits N * 700ms (0.7s, 1.4s)

    const sendToBackend = async (apiData, corrId, groupID, feeSkus = {}) => {
        const apiBaseUrl = getApiBaseUrl();
        const endpoint = 'api/shopify/addtocart';
        const itemsCount = apiData?.data?.items?.length ?? 0;

        console.log('[CartAPI] Sending to backend:', {
            url: apiBaseUrl + endpoint,
            corrId: corrId,
            itemsCount
        });

        for (let attempt = 1; attempt <= SEND_TO_BACKEND_MAX_ATTEMPTS; attempt++) {
            try {
                const response = await axios.post(
                    apiBaseUrl + endpoint,
                    apiData,
                    { headers: { 'X-Client-Request-Id': corrId } }
                );

                // Parse response
                let lineItems = {};
                if (typeof response.data === 'string') {
                    lineItems = getJsonResponseFromString(response.data);
                } else {
                    lineItems = response.data.responesVariantsList;
                }

                if (!lineItems || !lineItems.items) {
                    throw new Error('Backend response missing items');
                }

                // Format fee properties using product data SKUs for non-zero fees
                const priceDetailsItem = Array.isArray(apiData?.data?.items)
                    ? apiData.data.items.find(i => i?.properties?._price_details)
                    : null;
                const priceDetails = priceDetailsItem?.properties?._price_details || {};

                // Format fee properties using product data SKUs for non-zero fees
                const productFeeProperties = formatLineItemsFeeForCart(
                    lineItems.items.product_fee.properties,
                    groupID,
                    feeSkus,
                    priceDetails
                );
                lineItems.items.product_fee.properties = productFeeProperties;

                if (attempt > 1) {
                    console.log(`[CartAPI] sendToBackend - succeeded on attempt ${attempt}`);
                }
                console.log('[CartAPI] Backend response processed successfully');

                logToBackend(
                    'debug',
                    'cart-add-response',
                    `sendToBackend OK <- ${endpoint} corr=${corrId} attempt=${attempt}/${SEND_TO_BACKEND_MAX_ATTEMPTS} status=${response.status}`,
                    corrId
                );

                return lineItems;

            } catch (error) {
                // axios only sets error.response when the browser was allowed to
                // read the HTTP response. A blocked/opaque response (the "CORS +
                // HTML" failure mode: backend throws an uncaught exception mid
                // request, so Laravel renders its default HTML error page and
                // skips every "after" middleware — including the one that sets
                // Access-Control-Allow-Origin — before the response ever reaches
                // the browser) shows up here as error.response === undefined and
                // error.message === 'Network Error', even though a real HTTP
                // response (with a body) was actually sent by the server.
                const isOpaqueOrNetworkError = !error.response;
                const status = error?.response?.status ?? 'n/a';
                const contentType = error?.response?.headers?.['content-type'] ?? 'n/a';
                const isFinalAttempt = attempt === SEND_TO_BACKEND_MAX_ATTEMPTS;

                // Whenever the response came back as text/html instead of JSON,
                // grab a snippet of the body. If APP_DEBUG is on backend-side,
                // Laravel's HTML error page contains the actual exception
                // message/file/line — capturing it here means we see the real
                // cause directly in this log, without having to go correlate
                // against backend logs at all. (contentType is 'n/a' in the
                // opaque/CORS-blocked case, so this naturally only fires when
                // there's an actual readable body to grab.)
                let bodySnippet = null;
                if (contentType.includes('text')) {
                    const rawBody = typeof error.response?.data === 'string'
                        ? error.response.data
                        : JSON.stringify(error.response?.data ?? '');
                    bodySnippet = rawBody.slice(0, 500);
                }

                console.error(`[CartAPI] Backend API error (attempt ${attempt}/${SEND_TO_BACKEND_MAX_ATTEMPTS}):`, error);

                // Only the exhausted final attempt is CRITICAL (triggers the
                // Grafana alert) — a retry that later succeeds is not an incident.
                logToBackend(
                    isFinalAttempt ? 'critical' : 'debug',
                    'cart-add-response-error',
                    `sendToBackend FAILED -> ${endpoint} corr=${corrId} attempt=${attempt}/${SEND_TO_BACKEND_MAX_ATTEMPTS} status=${status} ` +
                    `contentType=${contentType} corsOrNetworkError=${isOpaqueOrNetworkError} ` +
                    `message="${error?.message || 'unknown'}"` +
                    (bodySnippet !== null ? ` bodySnippet="${bodySnippet}"` : ''),
                    corrId
                );

                if (isFinalAttempt) {
                    throw error;
                }

                const wait = attempt * SEND_TO_BACKEND_RETRY_BASE_MS;
                console.warn(`[CartAPI] sendToBackend - attempt ${attempt} failed, retrying in ${wait}ms`);
                await _delay(wait);
            }
        }
    };

    /**
     * Complete Add to Cart flow
     * 
     * 1. Send to backend
     * 2. Add fees to cart (first)
     * 3. Add products to cart (second)
     * 4. Fetch cart data
     * 5. Redirect to cart page
     * 
     * @param {Object} params - Parameters
     * @param {Object} params.apiData - Data for backend
     * @param {string} params.corrId - Correlation ID
     * @param {string} params.groupID - Group ID
     * @param {boolean} params.cartState - Cart state (false = fee first, true = items first)
     * @param {Object} params.cartObject - Cart object reference
     * @param {Object} params.responsedCartItems - Responded cart items reference
     * @param {Object} params.feeSkus - Fee SKUs from product imprint_data
     * @param {Function} params.onSuccess - Success callback
     * @param {Function} params.onError - Error callback
     */
    const completeAddToCart = async ({
        apiData,
        corrId,
        groupID,
        cartState,
        cartObject,
        responsedCartItems,
        feeSkus = {},
        onSuccess,
        onError
    }) => {
        try {
            // 1. Send to backend
            const lineItems = await sendToBackend(apiData, corrId, groupID, feeSkus);
            
            // 2. Prepare items for cart
            const feeCartLine = [lineItems.items.product_fee];
            const productItems = [];
            
            for (const [key, value] of Object.entries(lineItems.items)) {
                if (key !== 'product_fee') {
                    productItems.push(value);
                }
            }
            
            if (productItems.length === 0 && !lineItems.items.product_fee) {
                throw new Error('Backend returned no items and no fee - cannot proceed');
            }
            
            // Determine order based on cartState
            let firstBatch, secondBatch;
            if (cartState) {
                firstBatch = productItems;
                secondBatch = feeCartLine;
            } else {
                firstBatch = feeCartLine;
                secondBatch = productItems;
            }
            
            // 3. Add first batch to cart
            await addToCartFirst(firstBatch, corrId, responsedCartItems);
            
            // 4. Add second batch to cart
            await addToCartSecond(secondBatch, corrId, responsedCartItems);
            
            // 5. Fetch cart data
            await getFromCart(cartObject, responsedCartItems);
            
            // 6. Cleanup localStorage
            localStorage.removeItem("stringProductList");
            localStorage.removeItem("fullStorageProductList");
            
            // 7. Trigger completion event
            if (window.jQuery("#add-to-cart-complited")) {
                window.jQuery("#add-to-cart-complited").trigger("click");
            }
            
            // 8. Call success callback
            if (onSuccess) {
                onSuccess();
            }
            
            console.log('[CartAPI] Add to cart completed successfully');
            
        } catch (error) {
            console.error('[CartAPI] Add to cart failed:', error);
            if (onError) {
                onError(error);
            }
            throw error;
        }
    };

    return {
        getApiBaseUrl,
        getShopDomain,
        getShopName,
        getFromCart,
        addToCartFirst,
        addToCartSecond,
        sendToBackend,
        completeAddToCart
    };
}
